import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import Select from 'react-select'
import { useSidebar } from '../../../Context/SidebarContext'
import { isMobile } from '../../../Constants'
import RouterOutlet from '../CompanyProfileRoutes'
import classes from './wizard.module.css'

// Navigation configuration
const NAV_ITEMS = [
  {
    path: '/companyprofilenew/generalinfo',
    label: 'General Information',
    icon: 'fa-info-circle'
  },
  {
    path: '/companyprofilenew/team',
    label: 'Organization and Team',
    icon: 'fa-users'
  },
  {
    path: '/companyprofilenew/masters/freightmaster',
    label: 'Masters',
    icon: 'fa-database',
    matchPattern: '/companyprofilenew/masters'
  },
  {
    path: '/companyprofilenew/tnc',
    label: 'Terms and Conditions',
    icon: 'fa-file-contract'
  },
  {
    path: '/companyprofilenew/operations',
    label: 'Operations Overview',
    icon: 'fa-tasks'
  },
  {
    path: '/companyprofilenew/transporters',
    label: 'My Transporters',
    icon: 'fa-truck'
  },
  {
    path: '/companyprofilenew/settings',
    label: 'Settings',
    icon: 'fa-cog'
  },
  {
    path: '/companyprofilenew/rfqdetails',
    label: 'RFQ Details',
    icon: 'fa-list-alt'
  },
  {
    path: '/companyprofilenew/reports',
    label: 'Report Generator',
    icon: 'fa-chart-bar'
  }
]

const Wizard = () => {
  const { isCollapsed, sidebarWidth, setSidebarWidth, toggleSidebar, isMobile: isMobileView } = useSidebar()
  const history = useHistory()
  const location = useLocation()
  const isResizing = useRef(false)

  // Mobile select state
  const [mobileSelectedValue, setMobileSelectedValue] = useState(NAV_ITEMS[0])

  // Update mobile select value based on current route
  useEffect(() => {
    if (isMobile() || isMobileView) {
      const currentItem = NAV_ITEMS.find(item => 
        item.matchPattern 
          ? location.pathname.includes(item.matchPattern)
          : location.pathname === item.path
      )
      if (currentItem) {
        setMobileSelectedValue({ label: currentItem.label, value: currentItem.path })
      }
    }
  }, [location.pathname, isMobileView])

  // Sidebar resize handlers
  const handleMouseDown = useCallback((e) => {
    isResizing.current = true
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'col-resize'
  }, [])

  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current) return
    const newWidth = e.clientX
    if (newWidth > 150 && newWidth < 500) {
      setSidebarWidth(newWidth)
    }
  }, [setSidebarWidth])

  const handleMouseUp = useCallback(() => {
    isResizing.current = false
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'default'
  }, [handleMouseMove])

  // Check if nav item is active
  const isNavItemActive = useCallback((item) => {
    return item.matchPattern 
      ? location.pathname.includes(item.matchPattern)
      : location.pathname === item.path
  }, [location.pathname])

  // Mobile view
  if (isMobile() || isMobileView) {
    return (
      <div className={classes['Container']}>
        <div className={classes['wizard-select']}>
          <Select
            options={NAV_ITEMS.map(item => ({ 
              label: item.label, 
              value: item.path 
            }))}
            value={mobileSelectedValue}
            onChange={(option) => history.push({ pathname: option.value })}
            className="Truck_Type_Select"
            classNamePrefix="Truck_Prefix"
          />
        </div>
        <div className={classes['body']}>
          <RouterOutlet />
        </div>
      </div>
    )
  }

  // Desktop view
  return (
    <div className={classes['Container']}>
      <div
        className={`${classes['sidebar-container']} ${isCollapsed ? classes['collapsed'] : ''} ${isResizing.current ? classes['is-resizing'] : ''}`}
        style={{ '--sidebar-width': `${isCollapsed ? 60 : sidebarWidth}px` }}
      >
        <div className={classes["wizard"]}>
          {NAV_ITEMS.map(item => (
            <Link
              key={item.path}
              to={{ pathname: item.path }}
              className={`${classes['wizard-item']} ${isNavItemActive(item) ? classes['active'] : ''}`}
            >
              <i className={`fas ${item.icon}`}></i>
              {!isCollapsed && <span>{item.label}</span>}
            </Link>
          ))}
        </div>
        {!isCollapsed && (
          <div className={classes['resizer']} onMouseDown={handleMouseDown} />
        )}
      </div>

      <button
        className={`${classes['floating-toggle']} ${isCollapsed ? classes['is-collapsed'] : ''}`}
        onClick={toggleSidebar}
        title={isCollapsed ? "Expand" : "Collapse"}
        style={{ '--sidebar-width': `${isCollapsed ? 60 : sidebarWidth}px` }}
      >
        <div className={classes['sidebar-toggle-icon']}></div>
      </button>

      <div className={classes['body']}>
        <RouterOutlet />
      </div>
    </div>
  )
}

export default Wizard